import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";

import {
  createFirmwareShare,
  fetchFirmwareShareGrantees,
  fetchFirmwareShares,
  firmwareShareGrantsKey,
  revokeFirmwareShare,
  type FirmwareShareGrant,
} from "./firmwareApi";

const MODAL_HELP =
  "Grant download access so someone can download this file. You can revoke that access later from this list.";

function grantPersonLabel(grant: FirmwareShareGrant): string {
  return grant.granteeDisplayName ?? grant.granteeEmail ?? grant.granteeUserId;
}

export function FirmwareShareModal({
  open,
  firmwareId,
  fileLabel,
  onClose,
}: {
  open: boolean;
  firmwareId: number;
  fileLabel: string;
  onClose: () => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [granteeSearch, setGranteeSearch] = useState("");
  const [selectedGranteeId, setSelectedGranteeId] = useState("");

  const grantsQuery = useQuery<{ grants: FirmwareShareGrant[] }, ApiError>({
    queryKey: firmwareShareGrantsKey(firmwareId),
    queryFn: () => fetchFirmwareShares(firmwareId),
    enabled: open,
  });

  const granteesQuery = useQuery({
    queryKey: ["engineering", "firmware", "share-grantees", granteeSearch],
    queryFn: () => fetchFirmwareShareGrantees(granteeSearch),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: (granteeUserId: string) => createFirmwareShare(firmwareId, granteeUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: firmwareShareGrantsKey(firmwareId) });
      setSelectedGranteeId("");
      setGranteeSearch("");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (grantId: string) => revokeFirmwareShare(firmwareId, grantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: firmwareShareGrantsKey(firmwareId) });
    },
  });

  const granteeOptions = useMemo(
    () =>
      (granteesQuery.data?.grantees ?? []).map((grantee) => ({
        value: grantee.userId,
        label: grantee.email ? `${grantee.label} (${grantee.email})` : grantee.label,
      })),
    [granteesQuery.data?.grantees],
  );

  const grants = grantsQuery.data?.grants ?? [];

  if (!open) {
    return null;
  }

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="firmware-share-title">
      <div className="my-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="firmware-share-title" className="text-lg font-semibold text-slate-900">
              Share {fileLabel}
            </h2>
            <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
          </div>
        </div>
        <div className="space-y-3 px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="block min-w-0 flex-1">
              <span className="block text-xs font-medium text-slate-600">Add person</span>
              <SearchableSelect
                className="mt-1"
                triggerClassName="h-9 text-sm"
                value={selectedGranteeId}
                onValueChange={(option) => setSelectedGranteeId(option?.value ?? "")}
                options={granteeOptions}
                searchValue={granteeSearch}
                onSearchChange={setGranteeSearch}
                placeholder="Choose hub user"
                searchPlaceholder="Search by name or email"
                emptyMessage="No matching users."
                isLoading={granteesQuery.isLoading}
              />
            </label>
            <ReadOnlyHoverLayer active={!selectedGranteeId} hoverHint="Choose a user first.">
              <Button
                type="button"
                variant="secondary"
                className="shrink-0"
                disabled={!selectedGranteeId || createMutation.isPending}
                isLoading={createMutation.isPending}
                onClick={() => {
                  if (!selectedGranteeId) return;
                  createMutation.mutate(selectedGranteeId);
                }}
              >
                <UserPlus className="mr-1.5 h-4 w-4" aria-hidden />
                Grant download access
              </Button>
            </ReadOnlyHoverLayer>
          </div>
          {createMutation.isError ? (
            <p className="text-sm text-rose-700">{createMutation.error.message}</p>
          ) : null}
          {grantsQuery.isLoading ? (
            <p className="text-xs text-slate-500">Loading shares…</p>
          ) : grants.length === 0 ? (
            <p className="text-xs text-slate-500">No download access granted yet.</p>
          ) : (
            <ul className="space-y-1">
              {grants.map((grant) => (
                <li
                  key={grant.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{grantPersonLabel(grant)}</div>
                    {grant.granteeEmail && grant.granteeDisplayName ? (
                      <div className="truncate text-xs text-slate-500">{grant.granteeEmail}</div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    aria-label={`Revoke download access for ${grantPersonLabel(grant)}`}
                    disabled={revokeMutation.isPending}
                    onClick={() => revokeMutation.mutate(grant.id)}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
